/**
 * 可参与样条插值的点结构。
 */
export interface SplineInterpolationPointLike {
    /** X 坐标。 */
    x: number;
    /** Y 坐标。 */
    y: number;
    /** Z 坐标。 */
    z: number;
}

/**
 * 样条连续重复点判定容差。
 * 说明：
 * 1. 点捕捉会产生完全一致的坐标；
 * 2. 样条插值对连续重复点敏感，需在进入 Worker 前做规范化；
 * 3. 容差保持极小，避免误伤正常的相邻控制点。
 */
export const SPLINE_INTERPOLATION_POINT_TOLERANCE = 1e-8;

/**
 * 判断两个样条插值点是否在容差内重合。
 * @param previousPoint 前一个点。
 * @param currentPoint 当前点。
 * @param tolerance 判定容差。
 * @returns 是否重合。
 */
export function areSplineInterpolationPointsCoincident(
    previousPoint: SplineInterpolationPointLike,
    currentPoint: SplineInterpolationPointLike,
    tolerance: number = SPLINE_INTERPOLATION_POINT_TOLERANCE,
): boolean {
    const dx = previousPoint.x - currentPoint.x;
    const dy = previousPoint.y - currentPoint.y;
    const dz = previousPoint.z - currentPoint.z;
    return dx * dx + dy * dy + dz * dz <= tolerance * tolerance;
}

/**
 * 压缩样条插值输入中的连续重复点。
 * 说明：
 * 1. 仅移除“连续重复”的控制点，保留非连续的同位点以维持闭合等语义；
 * 2. 返回新数组，但复用原点对象引用，避免额外坐标复制；
 * 3. 该规范化用于普通样条插值，不改变贝塞尔/B 样条的控制点语义。
 * @param points 原始控制点列表。
 * @param tolerance 判定容差。
 * @returns 去除连续重复点后的控制点列表。
 */
export function normalizeSplineInterpolationPoints<T extends SplineInterpolationPointLike>(
    points: readonly T[],
    tolerance: number = SPLINE_INTERPOLATION_POINT_TOLERANCE,
): T[] {
    if (points.length <= 1) {
        return [...points];
    }

    const normalizedPoints: T[] = [points[0]];
    for (let index = 1; index < points.length; index += 1) {
        const currentPoint = points[index];
        const previousPoint = normalizedPoints[normalizedPoints.length - 1];

        // 连续重复点会导致样条插值不稳定，这里统一压缩掉。
        if (areSplineInterpolationPointsCoincident(previousPoint, currentPoint, tolerance)) {
            continue;
        }
        normalizedPoints.push(currentPoint);
    }
    return normalizedPoints;
}
